import { Contact } from 'src/api/generated';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Avatar } from 'src/modules/shared/components/atoms/avatar';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { formatPhoneNumber } from 'src/modules/shared/utils/formatting/phone.util';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { PiCaretRight, PiUser, PiUsers } from 'react-icons/pi';
import { motion, AnimatePresence } from 'framer-motion';

interface ContactInfoProps {
  contacts: Contact[];
  phoneNumber: string;
  onOpenProfile: (contactId: number) => void;
  loading?: boolean;
}

export const ContactInfo: React.FC<ContactInfoProps> = ({
  contacts,
  phoneNumber,
  onOpenProfile,
  loading = false,
}) => {
  const primaryContact = contacts[0];
  const otherContacts = contacts.slice(1);
  const hasMultipleContacts = contacts.length > 1;

  const formattedNumber = formatPhoneNumber(phoneNumber);

  if (loading) {
    return (
      <div className="flex flex-col items-center animate-pulse">
        <div className="w-16 h-16 bg-gray-200 rounded-full" />
        <div className="mt-2 h-4 w-32 bg-gray-200 rounded" />
        <div className="mt-1 h-3 w-24 bg-gray-200 rounded" />
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2">
        <Avatar text={formattedNumber} size="lg" />

        <Text size="small" color="primary" weight="medium">
          {formattedNumber}
        </Text>
      </div>
    );
  }

  const primaryName = formatFullName(
    primaryContact.firstName,
    primaryContact.lastName
  );

  return (
    <div className="flex flex-col items-center gap-2">
      <Avatar text={primaryName || formattedNumber} size="lg" />

      <div className="flex flex-col items-center gap-2 w-full">
        <Button
          variant="secondary"
          appearance="soft"
          size="sm"
          onClick={() => onOpenProfile(primaryContact.id)}
        >
          <div className="flex items-center gap-2 w-full">
            <Text
              size="lead"
              color="secondary"
              weight="medium"
              className="truncate whitespace-nowrap max-w-[12rem]"
            >
              {/* TODO: check when implementing i18n */}
              {primaryName || 'Sin nombre'}
            </Text>

            <div className="flex items-center">
              <PiUser className="w-4 h-4" />
              <PiCaretRight className="h-4 w-4" />
            </div>
          </div>
        </Button>

        <Text size="small" color="secondary">
          {formattedNumber}
        </Text>
      </div>

      {hasMultipleContacts && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 w-full"
          >
            <div className="bg-info-50 rounded-lg px-3 py-2 w-full">
              <div className="flex items-center gap-2 mb-1">
                <PiUsers className="w-4 h-4 text-info-600" />
                <Text size="tiny" color="info" weight="medium">
                  {/* TODO: check when implementing i18n */}
                  {otherContacts.length} contacto
                  {otherContacts.length > 1 ? 's' : ''} más con este número
                </Text>
              </div>

              <div className="flex flex-col gap-1 max-h-20 overflow-y-auto">
                {otherContacts.slice(0, 3).map((contact) => {
                  const name = formatFullName(
                    contact.firstName,
                    contact.lastName
                  );

                  return (
                    <Button
                      key={contact.id}
                      onClick={() => onOpenProfile(contact.id)}
                      variant="info"
                      appearance="soft"
                      size="sm"
                      className="flex w-full rounded p-2 group my-1"
                    >
                      <div className="flex items-center gap-2 w-full">
                        {/* TODO: check when implementing i18n */}
                        <Avatar text={name || 'Sin nombre'} size="2xs" />

                        <Text size="tiny" color="primary" className="truncate">
                          {/* TODO: check when implementing i18n */}
                          {name || 'Sin nombre'}
                        </Text>

                        <div className="flex items-center gap-0 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                          <PiUser className="w-4 h-4" />
                          <PiCaretRight className="h-4 w-4" />
                        </div>
                      </div>
                    </Button>
                  );
                })}

                {otherContacts.length > 3 && (
                  <Text size="tiny" color="secondary" className="italic pl-2">
                    {/* TODO: check when implementing i18n */}Y más...
                  </Text>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  );
};
